import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';

type HeroSlide = {
  id: string;
  imageUrl: string;
  headline: string;
  subheadline?: string | null;
  buttonLabel?: string | null;
  buttonLink?: string | null;
};

const API_BASE =
  process.env.REACT_APP_API_BASE || 'http://localhost:3001';

/** Show site value line when CMS subheadline is missing or too short (e.g. placeholders). */
const SUBHEADLINE_MIN_FOR_HIDE_SUPPORT = 48;

function useFallbackSlides(t: (k: string) => string): HeroSlide[] {
  return React.useMemo(
    () => [
      {
        id: 'fallback',
        imageUrl: '/landing-hero.png',
        headline: t('storefront.hero_fallback_headline'),
        subheadline: t('storefront.hero_fallback_subheadline'),
        buttonLabel: t('storefront.hero_shop_now'),
        buttonLink: '#products',
      },
    ],
    [t],
  );
}

function normalizeImageUrl(raw: string): string {
  if (!raw) return '/landing-hero.png';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/uploads/')) return `${API_BASE}${raw}`;
  return raw;
}

function PublicHeroCarousel() {
  const { t, i18n } = useTranslation();
  const fallbackSlides = useFallbackSlides(t);
  const [slides, setSlides] = React.useState<HeroSlide[]>(fallbackSlides);
  const [current, setCurrent] = React.useState(0);
  const navigate = useNavigate();

  React.useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await fetch(
          applyContentLocaleToUrl(
            `${API_BASE}/content/hero-slides`,
            i18n.resolvedLanguage || i18n.language,
          ),
          {
            credentials: 'include',
          },
        );
        if (!res.ok) return;
        const data = await res.json();
        if (isMounted && Array.isArray(data) && data.length > 0) {
          setSlides(
            data.map((s: any) => ({
              id: s.id,
              imageUrl: normalizeImageUrl(s.imageUrl),
              headline: s.headline,
              subheadline: s.subheadline,
              buttonLabel: s.buttonLabel,
              buttonLink: s.buttonLink,
            })),
          );
          setCurrent(0);
        }
      } catch {
        // ignore, keep fallback
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  React.useEffect(() => {
    if (slides.length <= 1) return;
    const id = window.setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [slides.length]);

  const active = slides[current] ?? fallbackSlides[0];
  const subTrim = active.subheadline?.trim() ?? '';
  const showValueProposition =
    subTrim.length === 0 || subTrim.length < SUBHEADLINE_MIN_FOR_HIDE_SUPPORT;
  const hasMultipleSlides = slides.length > 1;

  const goPrev = () => {
    setCurrent((c) => (c - 1 + slides.length) % slides.length);
  };

  const goNext = () => {
    setCurrent((c) => (c + 1) % slides.length);
  };

  const handlePrimaryClick = () => {
    const target = active.buttonLink || '#products';
    if (target.startsWith('#')) {
      const el = document.querySelector(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(target);
    }
  };

  const trustItems = [
    { key: 'fresh', icon: 'fa-leaf', label: t('storefront.hero_trust_fresh') },
    { key: 'stock', icon: 'fa-boxes-stacked', label: t('storefront.hero_trust_stock') },
    { key: 'secure', icon: 'fa-shield-halved', label: t('storefront.hero_trust_secure') },
  ] as const;

  return (
    <section
      id="home"
      className="storefront-hero-flush relative mb-0 min-h-screen bg-slate-900 text-white"
    >
      <div className="relative w-full px-0">
        <div className="overflow-hidden rounded-none">
          <div className="storefront-hero-slide relative">
            <img
              src={active.imageUrl}
              alt={active.headline}
              className="h-full w-full object-cover"
            />
            {/* Readability: strong left column + soft vignette */}
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/25 sm:via-slate-900/60 sm:to-slate-900/15"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/25"
              aria-hidden
            />

            <div className="absolute inset-0 flex items-center">
              <div className="mx-auto flex w-full max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
                <div className="relative w-full max-w-xl rounded-3xl border border-white/15 bg-black/40 p-8 shadow-2xl backdrop-blur-md sm:max-w-2xl sm:p-10 lg:p-12">
                  <div
                    className="absolute left-0 top-8 h-16 w-1 rounded-full sm:top-10 sm:h-20"
                    style={{ backgroundColor: 'var(--storefront-brand-primary, #059669)' }}
                    aria-hidden
                  />
                  <div className="pl-5 sm:pl-6">
                    <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-300/95">
                      {t('storefront.hero_badge')}
                    </p>
                    <h2 className="text-balance text-3xl font-extrabold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl xl:text-[3.25rem]">
                      {active.headline}
                    </h2>

                    {subTrim ? (
                      <p className="mt-4 text-base font-medium leading-relaxed text-white/95 sm:text-lg">
                        {active.subheadline}
                      </p>
                    ) : null}

                    {showValueProposition ? (
                      <p className="mt-4 max-w-lg text-sm leading-relaxed text-white/75 sm:text-base">
                        {t('storefront.hero_value_proposition')}
                      </p>
                    ) : null}

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handlePrimaryClick}
                        className="inline-flex items-center justify-center rounded-full px-8 py-3.5 text-sm font-semibold uppercase tracking-wide text-white shadow-lg transition hover:brightness-[0.92]"
                        style={{
                          backgroundColor: 'var(--storefront-brand-primary, #059669)',
                          boxShadow:
                            '0 10px 25px color-mix(in srgb, var(--storefront-brand-primary, #059669) 38%, transparent)',
                        }}
                      >
                        {active.buttonLabel || t('storefront.hero_shop_now')}
                      </button>
                    </div>

                    <div className="mt-8 border-t border-white/10 pt-6">
                      <ul className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-x-4 sm:gap-y-2">
                        {trustItems.map((item) => (
                          <li
                            key={item.key}
                            className="flex items-center gap-2 text-xs font-medium text-white/85 sm:text-sm"
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 text-emerald-200/95">
                              <i className={`fa ${item.icon} text-sm`} aria-hidden />
                            </span>
                            {item.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {hasMultipleSlides ? (
              <>
                <button
                  type="button"
                  onClick={goPrev}
                  aria-label={t('storefront.hero_prev')}
                  className="absolute left-1 top-1/2 z-20 flex -translate-y-1/2 rounded-full border border-white/20 bg-black/35 p-2 text-white shadow-sm backdrop-blur-sm transition hover:border-white/40 hover:bg-black/55 sm:left-4 sm:p-2.5"
                >
                  <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  aria-label={t('storefront.hero_next')}
                  className="absolute right-1 top-1/2 z-20 flex -translate-y-1/2 rounded-full border border-white/20 bg-black/35 p-2 text-white shadow-sm backdrop-blur-sm transition hover:border-white/40 hover:bg-black/55 sm:right-4 sm:p-2.5"
                >
                  <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                </button>
                <div className="pointer-events-none absolute inset-x-0 bottom-8 flex flex-col items-center gap-3 sm:bottom-10">
                  <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-white/10 bg-black/25 px-3 py-2 backdrop-blur-md">
                    {slides.map((s, index) => {
                      const isActive = index === current;
                      return (
                        <button
                          key={s.id ?? `dot-${index}`}
                          type="button"
                          aria-label={t('storefront.hero_slide_aria', { n: index + 1 })}
                          aria-current={isActive ? 'true' : undefined}
                          onClick={() => setCurrent(index)}
                          className={`h-2 w-2 rounded-full border border-white/60 transition-all sm:h-2.5 sm:w-2.5 ${
                            isActive
                              ? 'scale-110 bg-white shadow-sm'
                              : 'bg-white/25 hover:bg-white/50'
                          }`}
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            ) : null}

            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[var(--storefront-body-bg,#f4f6f8)] sm:h-20" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default PublicHeroCarousel;
